import { gameState } from '../core/GameState.js';
import {
  TUNING, CONFIG_DEFAULTS, CONFIG_STORAGE_KEY, getTunable, setTunable,
} from '../core/Constants.js';

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/**
 * In-game tuning panel for the values in Constants.TUNING — toggle with the
 * backquote key or the CONFIG button. Changes persist to localStorage and are
 * re-applied at boot (see Constants.js). Entries marked ⟳ only take effect
 * when the level is rebuilt; the REBUILD button lights up to say so.
 */
export class ConfigPanel {
  constructor(game) {
    this.game = game;
    this.rows = new Map(); // path -> { range, num }
    this.buildDom();

    this.onKey = (e) => {
      if (e.code !== 'Backquote' || e.repeat) return;
      if (e.target?.closest?.('input, textarea, select')) return;
      this.toggle();
    };
    window.addEventListener('keydown', this.onKey);

    const btn = document.getElementById('config-btn');
    if (btn) {
      this.onButton = () => this.toggle();
      btn.addEventListener('click', this.onButton);
    }
  }

  buildDom() {
    const el = document.createElement('div');
    el.id = 'config-panel';
    el.className = 'hidden';

    const head = document.createElement('div');
    head.className = 'cfg-head';
    const title = document.createElement('span');
    title.textContent = 'CONFIG';
    const close = document.createElement('button');
    close.className = 'cfg-close';
    close.type = 'button';
    close.textContent = '✕';
    close.addEventListener('click', () => this.toggle());
    head.append(title, close);
    el.appendChild(head);

    const body = document.createElement('div');
    body.className = 'cfg-body';
    let sectionEl = null;
    let section = null;
    for (const t of TUNING) {
      if (t.section !== section) {
        section = t.section;
        sectionEl = document.createElement('div');
        sectionEl.className = 'cfg-section';
        const h = document.createElement('h2');
        h.textContent = section;
        sectionEl.appendChild(h);
        body.appendChild(sectionEl);
      }
      sectionEl.appendChild(this.makeRow(t));
    }
    el.appendChild(body);

    const foot = document.createElement('div');
    foot.className = 'cfg-foot';
    this.rebuildBtn = this.makeButton('REBUILD LEVEL', () => {
      this.game.buildLevel();
      this.rebuildBtn.classList.remove('dirty');
    });
    foot.append(
      this.rebuildBtn,
      this.makeButton('RESET DEFAULTS', () => this.reset()),
    );
    el.appendChild(foot);

    document.body.appendChild(el);
    this.el = el;
  }

  makeButton(label, onClick) {
    const b = document.createElement('button');
    b.className = 'btn ghost';
    b.type = 'button';
    b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  }

  makeRow(t) {
    const row = document.createElement('label');
    row.className = 'cfg-row';

    const name = document.createElement('span');
    name.className = 'cfg-label';
    name.textContent = t.label;
    if (t.mode === 'build') {
      const badge = document.createElement('em');
      badge.className = 'cfg-badge';
      badge.title = 'Applies on level rebuild';
      badge.textContent = '⟳';
      name.appendChild(badge);
    }

    const range = document.createElement('input');
    range.type = 'range';
    const num = document.createElement('input');
    num.type = 'number';
    for (const input of [range, num]) {
      input.min = t.min;
      input.max = t.max;
      input.step = t.step;
      input.value = getTunable(t.path);
    }

    // Sync the control that isn't being edited, so typing "0." isn't fought.
    range.addEventListener('input', () => {
      const v = clamp(parseFloat(range.value), t.min, t.max);
      if (Number.isNaN(v)) return;
      num.value = v;
      this.commit(t, v);
    });
    num.addEventListener('input', () => {
      const raw = parseFloat(num.value);
      if (Number.isNaN(raw)) return;
      const v = clamp(raw, t.min, t.max);
      range.value = v;
      this.commit(t, v);
    });
    num.addEventListener('change', () => {
      num.value = getTunable(t.path); // snap to the clamped value on commit
    });

    row.append(name, range, num);
    this.rows.set(t.path, { range, num });
    return row;
  }

  commit(t, v) {
    setTunable(t.path, v);
    this.applySideEffects(t);
    this.save();
  }

  applySideEffects(t) {
    if (t.mode === 'fit') {
      this.game.cameraRig.fit(window.innerWidth / window.innerHeight);
    } else if (t.mode === 'build') {
      this.rebuildBtn.classList.add('dirty');
    }
    if (t.path === 'AUDIO.MASTER' && this.game.audio.master && !gameState.game.muted) {
      this.game.audio.master.gain.value = getTunable(t.path);
    }
    if (t.path === 'CURRY.IMMUNITY_CHANCE' || t.path === 'CURRY.POISON_CHANCE') {
      this.refreshInputs();
    }
  }

  /** Store only what differs from the shipped defaults. */
  save() {
    const overrides = {};
    for (const t of TUNING) {
      const v = getTunable(t.path);
      if (v !== CONFIG_DEFAULTS[t.path]) overrides[t.path] = v;
    }
    try {
      localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(overrides));
    } catch { /* storage unavailable — session-only tuning */ }
  }

  reset() {
    for (const t of TUNING) {
      setTunable(t.path, CONFIG_DEFAULTS[t.path]);
      this.applySideEffects(t);
    }
    try { localStorage.removeItem(CONFIG_STORAGE_KEY); } catch { /* ignore */ }
    this.refreshInputs();
  }

  refreshInputs() {
    for (const t of TUNING) {
      const row = this.rows.get(t.path);
      if (!row) continue;
      row.range.value = getTunable(t.path);
      row.num.value = getTunable(t.path);
    }
  }

  toggle() {
    this.el.classList.toggle('hidden');
  }

  dispose() {
    window.removeEventListener('keydown', this.onKey);
    document.getElementById('config-btn')?.removeEventListener('click', this.onButton);
    this.el.remove();
  }
}
